import { Fragment, type ElementType } from "react";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import { alpha, useTheme } from "@mui/material/styles";
import EventIcon from "@mui/icons-material/Event";
import InventoryIcon from "@mui/icons-material/Inventory";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineItem,
  TimelineSeparator,
} from "@mui/lab";
import MDBox from "components/MDBox";
import type { PedidoOperacionalInput } from "interfaces/loja/pedidoOperacional";
import { buildTimelineOperacional } from "utils/pedidoOperacional";

type PedidoTimelineOperacionalProps = {
  input: PedidoOperacionalInput;
};

function iconeDaEtapa(
  id: string,
  tipoEntrega: string | null | undefined
): ElementType {
  switch (id) {
    case "rec":
      return ShoppingBagOutlinedIcon;
    case "pay":
      return PaymentsIcon;
    case "fat":
      return ReceiptLongIcon;
    case "sep":
      return InventoryIcon;
    case "log":
      return LocalShippingIcon;
    case "ret":
      return tipoEntrega === "RETIRADA_EVENTO" ? EventIcon : StorefrontIcon;
    case "dig":
      return CloudDownloadOutlinedIcon;
    default:
      return ShoppingBagOutlinedIcon;
  }
}

export default function PedidoTimelineOperacional({
  input,
}: PedidoTimelineOperacionalProps) {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const primary = theme.palette.warning.main;

  const itens = buildTimelineOperacional(input);
  const tipoEntrega = input.tipoEntrega;

  const atualIdx = itens.findIndex((x) => x.atual);
  const etapaAtualLabel =
    atualIdx >= 0 ? itens[atualIdx].titulo : itens[itens.length - 1]?.titulo;

  const corMarcador = (variante: string, atual: boolean): string => {
    if (atual) return primary;
    if (variante === "success") return theme.palette.success.main;
    if (variante === "warning") return theme.palette.warning.main;
    if (variante === "error") return theme.palette.error.main;
    return theme.palette.grey[500];
  };

  const headerWash = alpha(primary, mode === "dark" ? 0.14 : 0.08);
  const borderSoft =
    mode === "dark" ? alpha("#c8e6c9", 0.35) : alpha(primary, 0.18);

  const connectorActive = (index: number) => {
    const a = itens[index];
    const b = itens[index + 1];
    if (!b) return false;
    return Boolean(a?.concluido || a?.atual);
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${borderSoft}`,
        overflow: "hidden",
        boxShadow:
          mode === "dark"
            ? `0 8px 32px ${alpha("#000", 0.35)}`
            : "0 8px 30px rgba(25, 118, 210, 0.08)",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 2.5, md: 3 },
          py: { xs: 2, md: 2.25 },
          background: `linear-gradient(115deg, ${headerWash} 0%, ${alpha(
            primary,
            0.02
          )} 42%, transparent 72%)`,
          borderBottom: `1px solid ${alpha(theme.borders.borderColor, 0.6)}`,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(primary, 0.15),
                color: primary,
              }}
            >
              <RouteOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <div>
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={700}
              >
                Operação
              </Typography>
              <Typography variant="h5" fontWeight={800} lineHeight={1.2}>
                Linha do tempo
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={0.25}
              >
                Etapas do pedido — o que já passou, onde está agora e o que
                falta
              </Typography>
            </div>
          </Stack>
          {etapaAtualLabel ? (
            <Chip
              size="medium"
              color="primary"
              variant="outlined"
              label={`Etapa atual: ${etapaAtualLabel}`}
              sx={{ fontWeight: 700, borderWidth: 1, padding: 2 }}
            />
          ) : null}
        </Stack>
      </Box>

      {/* Desktop: pipeline horizontal */}
      <MDBox
        sx={{
          display: { xs: "none", md: "block" },
          px: 2,
          py: 3,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          {itens.map((it, index) => {
            const Icon = iconeDaEtapa(it.id, tipoEntrega);
            const main = corMarcador(it.variante, it.atual);
            const isLast = index === itens.length - 1;

            return (
              <Fragment key={it.id}>
                <Box
                  sx={{
                    flex: "1 1 0",
                    minWidth: 0,
                    textAlign: "center",
                    px: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      mx: "auto",
                      borderRadius: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha(main, it.atual ? 0.22 : 0.1),
                      color: main,
                      border: `2px solid ${alpha(main, it.atual ? 0.55 : 0.35)}`,
                      boxShadow: it.atual
                        ? `0 0 0 4px ${alpha(primary, 0.2)}, 0 4px 14px ${alpha(main, 0.25)}`
                        : undefined,
                      transition: "box-shadow 0.2s ease, transform 0.2s ease",
                      ...(it.atual && {
                        animation: "pulseEtapa 2.2s ease-in-out infinite",
                        "@keyframes pulseEtapa": {
                          "0%, 100%": {
                            boxShadow: `0 0 0 4px ${alpha(primary, 0.15)}`,
                          },
                          "50%": {
                            boxShadow: `0 0 0 8px ${alpha(primary, 0.08)}`,
                          },
                        },
                      }),
                    }}
                    aria-hidden
                  >
                    <Icon sx={{ fontSize: 26 }} />
                  </Box>
                  <Typography
                    variant="caption"
                    fontWeight={it.atual ? 800 : 700}
                    display="block"
                    mt={1.25}
                    lineHeight={1.25}
                  >
                    {it.titulo}
                  </Typography>
                  {it.subtitulo ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mt={0.35}
                      sx={{ fontSize: "0.7rem", lineHeight: 1.3 }}
                    >
                      {it.subtitulo}
                    </Typography>
                  ) : null}
                </Box>
                {!isLast ? (
                  <Box
                    sx={{
                      flex: "0 0 14px",
                      flexShrink: 0,
                      pt: "26px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        width: "100%",
                        borderRadius: 2,
                        bgcolor: connectorActive(index)
                          ? alpha(theme.palette.success.main, 0.7)
                          : alpha(
                              theme.palette.text.primary,
                              mode === "dark" ? 0.15 : 0.1
                            ),
                      }}
                    />
                  </Box>
                ) : null}
              </Fragment>
            );
          })}
        </Stack>
      </MDBox>

      {/* Mobile: timeline vertical */}
      <MDBox
        sx={{
          display: { xs: "block", md: "none" },
          px: 2,
          pb: 2.5,
          pt: 1,
        }}
      >
        <Timeline
          sx={{
            p: 0,
            m: 0,
            [`& .MuiTimelineItem-root:before`]: { flex: 0, padding: 0 },
          }}
        >
          {itens.map((it, index) => {
            const Icon = iconeDaEtapa(it.id, tipoEntrega);
            const main = corMarcador(it.variante, it.atual);
            const lineColor = alpha(
              theme.palette.text.primary,
              mode === "dark" ? 0.22 : 0.14
            );

            return (
              <TimelineItem key={it.id} sx={{ minHeight: 76 }}>
                <TimelineSeparator>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      bgcolor: alpha(main, it.atual ? 0.22 : 0.12),
                      color: main,
                      border: `2px solid ${alpha(main, 0.45)}`,
                      boxShadow: it.atual
                        ? `0 0 0 3px ${alpha(primary, 0.18)}`
                        : undefined,
                    }}
                    aria-hidden
                  >
                    <Icon sx={{ fontSize: 22 }} />
                  </Box>
                  {index < itens.length - 1 ? (
                    <TimelineConnector
                      sx={{
                        width: 2,
                        flex: 1,
                        minHeight: 10,
                        bgcolor: lineColor,
                        borderRadius: 1,
                      }}
                    />
                  ) : null}
                </TimelineSeparator>
                <TimelineContent sx={{ py: "12px", pl: 1.5, pr: 0 }}>
                  <Stack spacing={0.35}>
                    <Typography
                      variant="body2"
                      fontWeight={it.atual ? 800 : 700}
                    >
                      {it.titulo}
                      {it.atual ? " · agora" : ""}
                    </Typography>
                    {it.subtitulo ? (
                      <Typography variant="caption" color="text.secondary">
                        {it.subtitulo}
                      </Typography>
                    ) : null}
                  </Stack>
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      </MDBox>
    </Card>
  );
}
